import { useEffect, useMemo, useState } from 'react'
import { X, Tag, Check, Users } from 'lucide-react'
import { apiGet, cachedGet, postJson } from '../../lib/api.js'
import { asList } from '../../lib/huddle.js'
import { apiErrorText } from '../../lib/apiError.js'
import {
  Badge, Banner, Checkbox, DrawerFooter, EmptyState, ErrorState, Field, Loading, Modal, NoMatchState,
  SearchInput, SearchSelect, useToast, THAI_COLLATOR,
} from '../../kit'

// The app's ONE promotion-catalog browser. Two callers, two meanings:
//
//   • Morning huddle (default) — PLAN the upsell for one appointment: "when this
//     patient arrives, pitch these". Writes `huddle_upsell` and never
//     promotion_patient; re-opening shows the current plan, saving replaces it,
//     and saving with nothing ticked clears it.
//   • With `onConfirm` — the caller owns the write and just wants the ticked ids.
//     The patient โปรโมชั่น page uses this to create promotion_patient rows (the
//     record of a real sale).
//
// Fetches the active promotion catalog (/api/promotions). Free-text search runs
// over EVERY active promotion, and the two dropdowns — ประเภท (promotion.Maintype,
// defaults to Upsale) and ชนิดย่อย (Subtype) — are rebuilt from whatever the search
// matched, so they can never offer an empty combination; a selection the search no
// longer contains falls back to ทั้งหมด.
// Multiple promotions can go on one patient in a single pass. In-app overlay
// (never a native dialog). Where promotion_patient rows ARE written, `seller` is
// stamped server-side from the session.
const DEFAULT_TYPE = 'Upsale'
const ALL = '__all__'
const NONE = '(ไม่ระบุ)'                      // bucket for a blank Maintype/Subtype
const bucket = (v) => String(v || '').trim() || NONE

// [value, count] pairs for a field, sorted Thai-aware.
function tally(list, field) {
  const n = new Map()
  for (const p of list) { const k = bucket(p[field]); n.set(k, (n.get(k) || 0) + 1) }
  return [...n.entries()].sort((a, b) => THAI_COLLATOR.compare(a[0], b[0]))
}

export default function PromotionPicker({
  patientId, patientName, planDate, branchId, appointmentId = 0, initial = [],
  // Staff already assigned to this plan (huddle mode only). Whole-state, like
  // `initial`: what is ticked at save time replaces what was stored.
  initialAssignees = [],
  // Promotions this patient ALREADY holds (active promotion_patient rows), and the
  // subset of those nothing has been billed against yet. `promotion_patient` has no
  // duplicate guard upstream and `promotion_patient/add` counts redemptions PER ROW,
  // so a second row for the same package gives the patient two independent counters
  // — a 3-use coupon becomes 6 — and re-arms api 189's commission guard, paying the
  // seller twice. Re-buying a used-up package is legitimate, so this marks rather
  // than blocks; only the still-unused case demands an explicit acknowledgement.
  held = [], heldUnused = [],
  // When supplied, this REPLACES the huddle save: the picker hands back the ticked
  // promotion ids and the caller decides what to write. The patient โปรโมชั่น page
  // uses it to create promotion_patient rows; without it the huddle behaviour is
  // exactly as before. Kept as one component because it is the only place in the
  // app that browses the promotion catalog, and a second copy would drift.
  onConfirm, confirmLabel, title, subtitle,
  onClose, onSaved,
}) {
  const [promos, setPromos] = useState(null)
  const [error, setError] = useState(null)
  const [q, setQ] = useState('')
  const [type, setType] = useState(DEFAULT_TYPE)
  const [sub, setSub] = useState(ALL)
  const [picked, setPicked] = useState(() => initial.map(Number))   // promotion ids, in the order ticked
  const [assignees, setAssignees] = useState(() => initialAssignees.map(Number).filter(Boolean))
  const [staff, setStaff] = useState(null)   // null = still loading / not applicable
  const [saving, setSaving] = useState(false)
  const toast = useToast()

  // Who can be assigned = who is rostered at this branch on this day. Huddle mode
  // only: with `onConfirm` the caller owns the write and there is no plan to
  // assign. Fails quiet — losing the roster must not block ticking promotions.
  useEffect(() => {
    if (onConfirm || !planDate || !branchId) { setStaff([]); return }
    let alive = true
    apiGet('/api/huddle-staff', { date: planDate, branch_id: branchId })
      .then((r) => { if (alive) setStaff(Array.isArray(r?.staff) ? r.staff : []) })
      .catch(() => { if (alive) setStaff([]) })
    return () => { alive = false }
  }, [onConfirm, planDate, branchId])

  const toggleAssignee = (id) => setAssignees((prev) => (
    prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
  ))

  useEffect(() => {
    cachedGet('/api/promotions')
      .then((r) => {
        // Catalog carries retired promotions too — only active ones can be offered.
        const list = asList(r).filter((p) => p.active !== false && p.active !== 0)
        setPromos(list)
        // Fall back to "ทั้งหมด" when this catalog has no Upsale bucket.
        if (!list.some((p) => bucket(p.Maintype) === DEFAULT_TYPE)) setType(ALL)
      })
      .catch((e) => setError(e.message || 'โหลดโปรโมชั่นไม่สำเร็จ'))
  }, [])

  // Search is the outermost filter — it sweeps the whole active catalog, ignoring
  // the two dropdowns. Everything below is derived from its result.
  const matched = useMemo(() => {
    const s = q.trim().toLowerCase()
    if (!s) return promos || []
    return (promos || []).filter((p) =>
      `${p.Name || ''} ${p.Maintype || ''} ${p.Subtype || ''} ${p.Detail || ''}`.toLowerCase().includes(s))
  }, [promos, q])

  // ประเภท options = distinct Maintype among the matches.
  const typeOptions = useMemo(() => ([
    { value: ALL, label: 'ทั้งหมด', count: matched.length },
    ...tally(matched, 'Maintype').map(([t, n]) => ({ value: t, label: t, count: n })),
  ]), [matched])

  // ชนิดย่อย options = distinct Subtype within the chosen ประเภท.
  const inType = useMemo(
    () => matched.filter((p) => type === ALL || bucket(p.Maintype) === type),
    [matched, type],
  )
  const subOptions = useMemo(() => ([
    { value: ALL, label: 'ทั้งหมด', count: inType.length },
    ...tally(inType, 'Subtype').map(([t, n]) => ({ value: t, label: t, count: n })),
  ]), [inType])

  // A selection the current search no longer contains falls back to ทั้งหมด,
  // so typing never lands the user on an empty list.
  useEffect(() => {
    if (type !== ALL && promos && !typeOptions.some((o) => o.value === type)) setType(ALL)
  }, [typeOptions, type, promos])
  useEffect(() => {
    if (sub !== ALL && !subOptions.some((o) => o.value === sub)) setSub(ALL)
  }, [subOptions, sub])

  const filtered = useMemo(
    () => inType.filter((p) => sub === ALL || bucket(p.Subtype) === sub),
    [inType, sub],
  )

  const toggle = (id) => setPicked((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))

  const heldSet = useMemo(() => new Set((held || []).map(Number)), [held])
  const heldUnusedSet = useMemo(() => new Set((heldUnused || []).map(Number)), [heldUnused])
  // Ticked packages the patient already holds AND has never used. Duplicating one of
  // these is almost certainly a mistake, so the confirm button is gated on an
  // explicit tick-box until the user says otherwise.
  const dupUnused = useMemo(
    () => picked.map(Number).filter((x) => heldUnusedSet.has(x)), [picked, heldUnusedSet])
  const [dupOk, setDupOk] = useState(false)
  useEffect(() => { if (!dupUnused.length) setDupOk(false) }, [dupUnused.length])
  const dupNames = useMemo(
    () => dupUnused.map((x) => (promos || []).find((p) => Number(p.id) === x)?.Name || `#${x}`), [dupUnused, promos])

  // What is ticked, BY NAME. The list below is filtered three ways (search, ประเภท,
  // ชนิดย่อย) and the footer only ever said "เลือกแล้ว N รายการ", so the moment a ticked
  // promotion fell outside the current filter — which is the normal case once you search
  // for the second one — there was nothing on screen saying what the N were. Re-opening a
  // saved plan had the same hole: the ticks were real but invisible.
  //
  // Falls back to `#id` rather than hiding the chip: a plan can carry a promotion that has
  // since been retired, and the catalog fetch drops those (active only). A chip nobody can
  // name is still a chip that must be visible, or saving would silently drop it.
  const promoById = useMemo(() => {
    const m = new Map()
    for (const p of promos || []) m.set(Number(p.id), p)
    return m
  }, [promos])
  const pickedChips = useMemo(
    () => picked.map(Number).map((id) => ({ id, name: promoById.get(id)?.Name || `#${id}` })),
    [picked, promoById])

  // One POST = the whole plan for this appointment (upsert upstream) — unless the
  // caller supplied onConfirm, in which case they own the write.
  async function save() {
    if (saving) return
    setSaving(true); setError(null)
    if (onConfirm) {
      // The caller owns the write, so it owns the success toast too.
      try { await onConfirm(picked); onSaved?.(picked) }
      catch (e) {
        // `e.done` lists what DID land — un-tick those so pressing the button again
        // retries only the remainder. Without this a partially-failed batch re-sends
        // rows that already exist, and promotion_patient has no duplicate guard.
        if (Array.isArray(e?.done) && e.done.length) {
          setPicked((prev) => prev.filter((x) => !e.done.includes(Number(x))))
        }
        setError(apiErrorText(e, 'บันทึกไม่สำเร็จ'))
        setSaving(false)
      }
      return
    }
    try {
      const plan = await postJson('/api/huddle-upsell', {
        plan_date     : planDate,
        branch_id     : Number(branchId) || 0,
        appointment_id: Number(appointmentId) || 0,
        patient_id    : patientId,
        promotion_id  : picked,
        // Whole-state upstream — always send it, or the stored assignees are cleared.
        assignee_id   : assignees,
      })
      toast.success('บันทึกแล้ว')
      onSaved?.(plan)
    } catch (e) {
      setError(e.message || 'บันทึกแผนไม่สำเร็จ')
      toast.error('บันทึกแผนไม่สำเร็จ', { detail: e.message })
      setSaving(false)
    }
  }

  // Closing WHILE SAVING unmounts this component with the write still in flight, so
  // its catch block sets state on a dead component and the "เพิ่มได้ 1 จาก 3 รายการ"
  // message is lost — leaving a reloaded list with one new package and no error, which
  // reads as "nothing happened" and invites a re-tick of all three. The write is
  // sequential and each POST is durable, so the modal stays put until it settles.
  // (The kit Modal's `busy` enforces exactly that: Esc, backdrop and ✕ do nothing while saving.)
  const requestClose = () => { if (!saving) onClose?.() }

  // Unsaved ticks → the Modal asks "ทิ้งการแก้ไข?" before a stray backdrop click loses them.
  const sameSet = (a, b) => a.length === b.length && a.every((x) => b.map(Number).includes(Number(x)))
  const dirty = !sameSet(picked, initial) || (!onConfirm && !sameSet(assignees, initialAssignees.map(Number).filter(Boolean)))
  const filtersActive = !!q.trim() || type !== DEFAULT_TYPE || sub !== ALL
  const clearFilters = () => { setQ(''); setType(promos?.some((p) => bucket(p.Maintype) === DEFAULT_TYPE) ? DEFAULT_TYPE : ALL); setSub(ALL) }
  const canSave = !saving && !(dupUnused.length > 0 && !dupOk) && !(!picked.length && !(onConfirm ? false : initial.length))

  const footer = (
    <div className="space-y-3">
      {/* โปรโมชั่นที่เลือก — the ticked promotions BY NAME. The list above is filtered,
          so this is the only place that always shows the whole selection; each chip
          un-ticks itself, which is also the only way to drop one that the current
          filter is hiding. */}
      {pickedChips.length > 0 && (
        <div>
          <div className="flex items-center gap-1.5 text-[12px] font-medium text-slate-500 mb-1.5">
            <Tag size={13} className="text-brand-500" /> โปรโมชั่นที่เลือก
            <span className="text-slate-400 font-normal">· {pickedChips.length} รายการ</span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {pickedChips.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => toggle(c.id)}
                disabled={saving}
                title={`เอา ${c.name} ออก`}
                className="inline-flex items-center gap-1.5 max-w-full rounded-full bg-white pl-2.5 pr-1.5 py-1 text-[12.5px] font-medium text-brand-700 ring-1 ring-brand-200 hover:ring-brand-400 hover:text-brand-800 disabled:opacity-50"
              >
                <span className="truncate">{c.name}</span>
                <X size={12} strokeWidth={2.5} className="flex-none text-brand-400" />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ผู้รับผิดชอบ — only the staff rostered at this branch today. A plan
          nobody owns is a plan nobody pitches, so this sits directly above the
          save button rather than behind a tab. */}
      {!onConfirm && staff !== null && (
        <div>
          <div className="flex items-center gap-1.5 text-[12px] font-medium text-slate-500 mb-1.5">
            <Users size={13} className="text-slate-400" /> ผู้รับผิดชอบ
            {assignees.length > 0 && <span className="text-slate-400 font-normal">· เลือกแล้ว {assignees.length}</span>}
          </div>
          {staff.length === 0 ? (
            <div className="text-[12px] text-slate-400">ไม่มีพนักงานลงเวรที่สาขานี้ในวันดังกล่าว</div>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {staff.map((m) => {
                const on = assignees.includes(m.employee_id)
                return (
                  <button
                    key={m.employee_id} type="button" onClick={() => toggleAssignee(m.employee_id)} disabled={saving}
                    title={m.type ? `${m.code} · ${m.type}` : m.code}
                    className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[12.5px] font-medium ring-1 transition-colors disabled:opacity-50 ${
                      on ? 'bg-brand-600 text-white ring-brand-600' : 'bg-white text-slate-600 ring-slate-200 hover:bg-slate-50'}`}
                  >
                    {on && <Check size={12} strokeWidth={3} className="flex-none" />}
                    {m.code}
                  </button>
                )
              })}
            </div>
          )}
        </div>
      )}

      {dupUnused.length > 0 && (
        <Banner tone="error">
          <span className="text-[12.5px] leading-snug">
            คนไข้มี <span className="font-semibold">{dupNames.join(' · ')}</span> อยู่แล้วและยังไม่ได้ใช้สิทธิ์
            — การเพิ่มซ้ำจะนับสิทธิ์<span className="font-semibold">แยกกันคนละชุด</span> และจ่ายค่าคอมมิชชั่นอีกครั้ง
          </span>
          <div className="mt-1.5">
            <Checkbox checked={dupOk} onChange={setDupOk} disabled={saving} label="ยืนยันว่าต้องการเพิ่มซ้ำ" />
          </div>
        </Banner>
      )}

      <DrawerFooter
        onCancel={requestClose}
        onSave={save}
        saving={saving}
        canSave={canSave}
        saveLabel={`${confirmLabel || 'บันทึกแผน'}${picked.length ? ` ${picked.length} รายการ` : ''}`}
        status={(
          <span className="flex items-center gap-1.5 min-w-0">
            <Tag size={14} className="text-brand-500 flex-none" />
            {picked.length
              ? <span className="truncate">เลือกแล้ว {picked.length} รายการ</span>
              : <span className="text-slate-400">{!onConfirm && initial.length ? 'บันทึกเพื่อล้างแผนนี้' : 'ยังไม่ได้เลือก'}</span>}
            {picked.length > 0 && (
              <button
                type="button"
                onClick={() => setPicked([])}
                disabled={saving}
                title="ล้างรายการที่เลือกทั้งหมด"
                className="flex-none inline-flex items-center gap-1 px-2 py-1 rounded-md text-[12.5px] text-slate-500 hover:text-rose-600 hover:bg-rose-50 disabled:opacity-40"
              >
                <X size={13} /> ล้างทั้งหมด
              </button>
            )}
          </span>
        )}
      />
    </div>
  )

  return (
    <Modal
      onClose={requestClose}
      title={title || 'วางแผนอัปเซล'}
      subtitle={subtitle || patientName}
      busy={saving}
      dirty={dirty}
      footer={footer}
    >
      {/* Search + the two dropdowns stay pinned while the list scrolls under them. */}
      <div className="sticky top-0 z-10 -mx-5 px-5 pb-2.5 mb-2 bg-white border-b border-slate-100 space-y-2">
        <SearchInput
          value={q}
          onChange={setQ}
          placeholder="ค้นหาโปรโมชั่นทั้งหมด — ชื่อ / ประเภท / รายละเอียด…"
          autoFocus
        />
        <div className="grid grid-cols-2 gap-2">
          <Field label="ประเภท" className="min-w-0">
            <SearchSelect
              options={typeOptions}
              value={type}
              onChange={(v) => { setType(v === '' ? ALL : v); setSub(ALL) }}
              placeholder="ทั้งหมด"
              searchPlaceholder="ค้นหาประเภท…"
            />
          </Field>
          <Field label="ชนิดย่อย" className="min-w-0">
            <SearchSelect
              options={subOptions}
              value={sub}
              onChange={(v) => setSub(v === '' ? ALL : v)}
              placeholder="ทั้งหมด"
              searchPlaceholder="ค้นหาชนิดย่อย…"
            />
          </Field>
        </div>
      </div>

      {error && (
        <ErrorState title={promos === null ? 'โหลดโปรโมชั่นไม่สำเร็จ' : 'บันทึกไม่สำเร็จ'} message={error} className="mb-2" />
      )}
      {promos === null ? (
        !error && <Loading />
      ) : filtered.length === 0 ? (
        filtersActive
          ? <NoMatchState onClear={clearFilters} title="ไม่พบโปรโมชั่นที่ตรงกับตัวกรอง" />
          : <EmptyState variant="plain" title="ไม่พบโปรโมชั่น" />
      ) : (
        <div className="-mx-3">
          {filtered.map((p) => {
            const on = picked.includes(p.id)
            const isHeld = heldSet.has(Number(p.id))
            const isHeldUnused = heldUnusedSet.has(Number(p.id))
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => toggle(p.id)}
                disabled={saving}
                className={`w-full text-left px-3 py-2.5 rounded-lg disabled:opacity-50 flex items-center gap-3 ${on ? 'bg-brand-50' : 'hover:bg-slate-50'}`}
              >
                <span className={`flex-none w-[18px] h-[18px] rounded-[5px] border flex items-center justify-center ${on ? 'bg-brand-500 border-brand-500 text-white' : 'border-slate-300 text-transparent'}`}>
                  <Check size={13} strokeWidth={3} />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <span className="text-[13.5px] font-medium text-slate-800 truncate">{p.Name || '—'}</span>
                    {isHeld && (
                      <Badge
                        tone={isHeldUnused ? 'rose' : 'amber'}
                        className="shrink-0"
                        title={isHeldUnused
                          ? 'คนไข้มีแพ็กเกจนี้อยู่แล้วและยังไม่ได้ใช้สิทธิ์ — การเพิ่มซ้ำจะนับสิทธิ์แยกกัน'
                          : 'คนไข้เคยซื้อแพ็กเกจนี้และใช้สิทธิ์ไปแล้วบางส่วน'}
                      >
                        {isHeldUnused ? 'มีอยู่แล้ว · ยังไม่ใช้' : 'มีอยู่แล้ว'}
                      </Badge>
                    )}
                  </div>
                  <div className="text-[12px] text-slate-500 truncate">
                    {[p.Maintype, p.Subtype].filter(Boolean).join(' · ')}{p.Detail ? ` — ${p.Detail}` : ''}
                  </div>
                </div>
              </button>
            )
          })}
        </div>
      )}
    </Modal>
  )
}
